import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Factory, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, PackageCheck, FlaskConical, QrCode, FileText, Tag, Copy, Printer, GitBranch } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

const expState = (s) => {
  if (!s) return { key: "nd", t: "DA DEFINIRE", c: "bg-muted text-muted-foreground" };
  const d = new Date(s); if (isNaN(d.getTime())) return { key: "nd", t: "DA DEFINIRE", c: "bg-muted text-muted-foreground" };
  const diff = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
  if (diff < 0) return { key: "exp", t: "SCADUTO", c: "bg-rose-500/15 text-rose-600" };
  if (diff <= 3) return { key: "soon", t: "IN SCADENZA", c: "bg-amber-500/15 text-amber-600" };
  return { key: "ok", t: "OK", c: "bg-emerald-500/15 text-emerald-600" };
};

const money = (n) => (n == null ? "COSTO N/D" : `€ ${Number(n).toFixed(2)}`);
const EXP = {
  ok: { label: "valido", cls: "bg-emerald-500/15 text-emerald-600" },
  expiring: { label: "vicino scad.", cls: "bg-amber-500/15 text-amber-600" },
  expired: { label: "scaduto", cls: "bg-rose-500/15 text-rose-600" },
  missing: { label: "scad. mancante", cls: "bg-muted text-muted-foreground" },
};
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function Produzioni() {
  const [list, setList] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [recipeId, setRecipeId] = useState("");
  const [qty, setQty] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmKey, setConfirmKey] = useState("");
  const [done, setDone] = useState(null);
  const [yieldQty, setYieldQty] = useState("");
  const [yieldUnit, setYieldUnit] = useState("porzioni");
  const [tree, setTree] = useState(null);
  const [detail, setDetail] = useState(null);
  const [qrProd, setQrProd] = useState(null);
  const [trace, setTrace] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const openTrace = async (batchId, label) => {
    try { const { data } = await api.get(`/productions/by-batch/${batchId}`); setTrace({ batchId, label, rows: data }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyLink = (token) => { navigator.clipboard?.writeText(`${window.location.origin}/produzione/${token}`); toast.success("Link copiato"); };

  const load = async () => {
    try {
      const [l, r] = await Promise.all([api.get("/productions"), api.get("/productions/recipes")]);
      setList(l.data); setRecipes(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startWizard = () => { setWizard(true); setStep(1); setRecipeId(""); setQty(""); setNotes(""); setPreview(null); setConfirmKey(uid()); setDone(null); setYieldQty(""); setYieldUnit("porzioni"); };
  const chosen = recipes.find((r) => r.id === recipeId);

  const runPreview = async () => {
    setLoadingPrev(true);
    try {
      const { data } = await api.post("/productions/preview", { recipe_id: recipeId, quantity_produced: Number(qty), production_unit: "porzioni" });
      setPreview(data); setStep(3);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingPrev(false); }
  };

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/productions/confirm", { recipe_id: recipeId, quantity_produced: Number(qty), production_unit: "porzioni", yield_quantity: yieldQty === "" ? null : Number(yieldQty), yield_unit: yieldUnit, notes, confirm_key: confirmKey });
      setDone(data); setStep(5); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) toast.error("Disponibilità/FEFO cambiati: ricontrolla l'anteprima");
      else toast.error(formatApiError(d));
    } finally { setConfirming(false); }
  };

  const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString("it-IT"); };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/productions/${id}`); setDetail(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openTree = async (id) => {
    try { const { data } = await api.get(`/productions/${id}/trace-tree`); setTree(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="productions-page">
      <PageHeader title="Produzioni" subtitle="Produzione reale con prelievo lotti FEFO e scarico magazzino" testid="productions-header" />
      <div className="flex justify-end mb-4">
        <Button onClick={startWizard} className="rounded-full" data-testid="new-production-btn"><Factory className="w-4 h-4 mr-1" />Nuova produzione</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Input placeholder="Cerca prodotto o lotto…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 max-w-xs" data-testid="prod-filter-search" />
        <div className="flex gap-1">
          {[["all", "Tutti"], ["ok", "OK"], ["soon", "In scadenza"], ["exp", "Scaduti"], ["nd", "Da definire"]].map(([k, l]) => (
            <Button key={k} size="sm" variant={statusFilter === k ? "default" : "outline"} className="rounded-full h-8 text-xs" onClick={() => setStatusFilter(k)} data-testid={`prod-filter-${k}`}>{l}</Button>
          ))}
        </div>
      </div>

      <Card className="p-5 rounded-xl">
        {list.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground" data-testid="productions-empty">
            <FlaskConical className="w-10 h-10 mx-auto mb-3 text-primary/60" />Nessuna produzione registrata.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-2">Lotto interno</th><th className="py-2 px-2">Prodotto</th><th className="py-2 px-2 text-center">Ver.</th>
                <th className="py-2 px-2">Data</th><th className="py-2 px-2 text-right">Q.tà</th><th className="py-2 px-2 text-right">Costo</th>
                <th className="py-2 px-2">Scad. interna</th><th className="py-2 px-2">Resp.</th><th className="py-2 px-2 text-center">Stato</th><th className="py-2 px-2 text-center">Azioni</th>
              </tr></thead>
              <tbody>
                {list.filter((p) => (!q || (p.recipe_name || "").toLowerCase().includes(q.toLowerCase()) || (p.internal_lot || "").toLowerCase().includes(q.toLowerCase())) && (statusFilter === "all" || expState(p.internal_expiry).key === statusFilter)).map((p) => {
                  const es = expState(p.internal_expiry);
                  return (
                  <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40" data-testid={`production-row-${p.id}`}>
                    <td className="py-2 px-2 font-mono font-medium cursor-pointer" onClick={() => openDetail(p.id)}>{p.internal_lot}</td>
                    <td className="py-2 px-2 cursor-pointer" onClick={() => openDetail(p.id)}>{p.recipe_name}</td>
                    <td className="py-2 px-2 text-center">v{p.recipe_version}</td>
                    <td className="py-2 px-2">{fmtDate(p.produced_at)}</td>
                    <td className="py-2 px-2 text-right">{p.quantity_produced} {p.production_unit}</td>
                    <td className="py-2 px-2 text-right">{money(p.total_cost_snapshot)}</td>
                    <td className="py-2 px-2"><span className={`text-xs px-2 py-0.5 rounded-full ${es.c}`}>{p.internal_expiry ? fmtDate(p.internal_expiry) : "DA DEFINIRE"}</span></td>
                    <td className="py-2 px-2 text-muted-foreground">{p.produced_by}</td>
                    <td className="py-2 px-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">{p.status}</span></td>
                    <td className="py-2 px-2">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setQrProd(p)} title="QR" className="p-1.5 rounded-lg hover:bg-muted" data-testid={`prod-qr-${p.id}`}><QrCode className="w-4 h-4 text-primary" /></button>
                        <a href={`/produzione/${p.public_token}`} target="_blank" rel="noreferrer" title="Scheda" className="p-1.5 rounded-lg hover:bg-muted" data-testid={`prod-sheet-${p.id}`}><FileText className="w-4 h-4 text-primary" /></a>
                        <a href={`/produzione/${p.public_token}?etichetta=1`} target="_blank" rel="noreferrer" title="Etichetta" className="p-1.5 rounded-lg hover:bg-muted" data-testid={`prod-label-${p.id}`}><Tag className="w-4 h-4 text-primary" /></a>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* WIZARD */}
      <Dialog open={wizard} onOpenChange={(o) => { if (!o) { setWizard(false); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="production-wizard">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Factory className="w-5 h-5 text-primary" />Nuova produzione · step {step > 4 ? 4 : step}/4</DialogTitle></DialogHeader>

          {step === 1 && (
            <div className="space-y-3">
              <Label>Ricetta operativa attiva</Label>
              <Select value={recipeId} onValueChange={setRecipeId}>
                <SelectTrigger data-testid="prod-recipe-select"><SelectValue placeholder="Seleziona ricetta…" /></SelectTrigger>
                <SelectContent>{recipes.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`prod-recipe-opt-${r.id}`}>{r.name} · v{r.recipe_version} · {r.ingredients_mapped}/{r.ingredients_count} mappati</SelectItem>)}</SelectContent>
              </Select>
              {chosen && (
                <Card className="p-3 bg-muted/40 text-sm space-y-1">
                  <p><b>{chosen.name}</b> — versione {chosen.recipe_version} · resa base {chosen.portions} porzioni</p>
                  {chosen.procedure && <p className="text-muted-foreground line-clamp-3">{chosen.procedure}</p>}
                  {chosen.ingredients_mapped < chosen.ingredients_count && <p className="text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Alcuni ingredienti non sono mappati: la produzione sarà bloccata.</p>}
                </Card>
              )}
              <div className="flex justify-end"><Button disabled={!recipeId} onClick={() => setStep(2)} className="rounded-full" data-testid="prod-step1-next">Avanti <ChevronRight className="w-4 h-4 ml-1" /></Button></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Quantità da produrre (porzioni/batch)</Label><Input type="number" min="0.1" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="prod-qty" /></div>
                <div className="space-y-1"><Label>Note (opz.)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="prod-notes" /></div>
              </div>
              <div className="rounded-xl border border-border p-3 space-y-2 bg-muted/30">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Resa ottenuta (lotto semilavorato)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Quantità resa (opz.)</Label><Input type="number" min="0" step="0.001" value={yieldQty} onChange={(e) => setYieldQty(e.target.value)} placeholder="es. 4250" data-testid="prod-yield-qty" /></div>
                  <div className="space-y-1"><Label className="text-xs">Unità di resa</Label>
                    <Select value={yieldUnit} onValueChange={setYieldUnit}>
                      <SelectTrigger data-testid="prod-yield-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>{["porzioni", "g", "kg", "ml", "l", "pz"].map((u) => <SelectItem key={u} value={u} data-testid={`prod-yield-unit-${u}`}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground">La resa definisce la quantità e l'unità del lotto interno riutilizzabile come ingrediente (es. Pasta Frolla in grammi). Se vuota, il lotto sarà pari alle porzioni prodotte.</p>
              </div>
              <p className="text-xs text-muted-foreground">Base ricetta: {chosen?.portions} porzioni. Le dosi verranno scalate automaticamente.</p>
              <div className="flex justify-between">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
                <Button disabled={!qty || Number(qty) <= 0 || loadingPrev} onClick={runPreview} className="rounded-full" data-testid="prod-step2-next">{loadingPrev ? "Calcolo FEFO…" : <>Anteprima FEFO <ChevronRight className="w-4 h-4 ml-1" /></>}</Button>
              </div>
            </div>
          )}

          {step === 3 && preview && (
            <div className="space-y-3">
              <div className="space-y-3">
                {preview.lines.map((l, i) => (
                  <Card key={i} className="p-3" data-testid={`prod-line-${l.ingredient_id || i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold">{l.name}</p>
                        <p className="text-xs text-muted-foreground">Richiesto {l.required_qty} {l.unit} ({l.required_base} {l.base_unit}) · disp. {l.available_base} {l.base_unit}</p>
                      </div>
                      <span className="text-sm font-semibold">{money(l.cost)}</span>
                    </div>
                    {l.blocking && <p className="text-xs text-rose-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />{l.blocking === "DISPONIBILITA_INSUFFICIENTE" ? `MANCANTE ${l.shortfall_base} ${l.base_unit}` : l.blocking.replaceAll("_", " ")}</p>}
                    <div className="mt-2 space-y-1">
                      {l.allocation.map((a, j) => {
                        const es = EXP[a.expiry_state] || EXP.missing;
                        return (
                          <div key={j} className="flex items-center justify-between text-xs border border-border rounded-lg px-2 py-1" data-testid={`prod-alloc-${a.batch_id}`}>
                            <span className="flex items-center gap-1.5 flex-wrap">{a.source_type === "internal_production" && <span className="px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600 text-[10px] font-semibold">semilavorato interno</span>}Lotto <b>{a.lotto || "—"}</b> · scad {a.expiry || "—"} · usa <b>{a.use_batch_unit} {a.batch_unit}</b>{a.is_override ? " (override)" : ""}</span>
                            <span className="flex items-center gap-2"><span className={`px-2 py-0.5 rounded-full ${es.cls}`}>{es.label}</span>{a.cost != null ? `€${a.cost.toFixed(2)}` : "costo n/d"}</span>
                          </div>
                        );
                      })}
                      {l.allocation.length === 0 && <p className="text-xs text-muted-foreground">Nessun lotto utilizzabile.</p>}
                    </div>
                  </Card>
                ))}
              </div>
              <Card className="p-3 bg-muted/40 text-sm flex items-center justify-between">
                <span>Costo totale stimato produzione</span><b>{money(preview.total_cost)}</b>
              </Card>
              {preview.shelf_life_warning && <p className="text-xs text-amber-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Shelf-life non configurata: la scadenza interna resterà "DA DEFINIRE".</p>}
              <div className="flex justify-between">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
                <Button className="rounded-full" onClick={() => setStep(4)} disabled={!preview.can_confirm} data-testid="prod-step3-next">{preview.can_confirm ? <>Riepilogo <ChevronRight className="w-4 h-4 ml-1" /></> : "Non confermabile"}</Button>
              </div>
            </div>
          )}

          {step === 4 && preview && (
            <div className="space-y-3">
              <Card className="p-4 space-y-1.5 text-sm">
                <p><b>{preview.recipe_name}</b> · versione {preview.recipe_version}</p>
                <p>Quantità prodotta: <b>{preview.quantity_produced} {preview.production_unit}</b></p>
                <p>Resa lotto semilavorato: <b>{yieldQty ? `${yieldQty} ${yieldUnit}` : `${preview.quantity_produced} porzioni`}</b></p>
                <p>Costo reale produzione: <b>{money(preview.total_cost)}</b></p>
                <p>Shelf-life: {preview.shelf_life_days ? `${preview.shelf_life_days} giorni` : "DA DEFINIRE"}</p>
                <p>Lotti utilizzati: {preview.lines.reduce((s, l) => s + l.allocation.length, 0)}</p>
              </Card>
              <p className="text-xs text-muted-foreground">Confermando verranno scaricati i lotti selezionati (FEFO) e creata una produzione immutabile con lotto interno.</p>
              <div className="flex justify-between">
                <Button variant="outline" className="rounded-full" onClick={() => setStep(3)}><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
                <Button className="rounded-full" onClick={confirm} disabled={confirming} data-testid="prod-confirm-btn"><PackageCheck className="w-4 h-4 mr-1" />{confirming ? "Conferma…" : "Conferma produzione"}</Button>
              </div>
            </div>
          )}

          {step === 5 && done && (
            <div className="space-y-3 text-center py-4" data-testid="prod-done">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
              <p className="text-lg font-bold">Produzione confermata</p>
              <p className="font-mono text-primary text-lg">{done.internal_lot}</p>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>{done.recipe_name} · v{done.recipe_version}</p>
                <p>{done.quantity_produced} {done.production_unit} · {money(done.total_cost_snapshot)}</p>
                <p>Scadenza interna: {done.internal_expiry ? fmtDate(done.internal_expiry) : "DA DEFINIRE"}</p>
                <p>{(done.ingredients || []).length} righe lotto scaricate</p>
              </div>
              <Button className="rounded-full" onClick={() => setWizard(false)} data-testid="prod-close">Chiudi</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* DETTAGLIO READ-ONLY */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="production-detail">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-mono">{detail.internal_lot}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-1">
                <p><b>{detail.recipe_name}</b> · versione {detail.recipe_version}</p>
                <p>Prodotto il {fmtDate(detail.produced_at)} da {detail.produced_by}</p>
                <p>Quantità: {detail.quantity_produced} {detail.production_unit} · Costo: {money(detail.total_cost_snapshot)}</p>
                {detail.quantity_remaining_produced != null && <p>Disponibile come semilavorato: <b>{detail.quantity_remaining_produced} {detail.yield_unit || detail.semi_unit || "porzioni"}</b></p>}
                <p>Scadenza interna: {detail.internal_expiry ? fmtDate(detail.internal_expiry) : "DA DEFINIRE"}</p>
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => openTree(detail.id)} data-testid="detail-trace-tree-btn"><GitBranch className="w-3.5 h-3.5 mr-1" />Albero tracciabilità</Button>
              </div>
              <h4 className="font-semibold mt-3 mb-1">Lotti utilizzati</h4>
              <div className="space-y-1.5">
                {(detail.ingredients || []).map((it, i) => (
                  <button key={i} onClick={() => openTrace(it.batch_id, it.name)} className="w-full text-left text-xs border border-border rounded-lg px-2 py-1.5 hover:bg-muted flex items-center justify-between gap-2" data-testid={`detail-lotto-${i}`}>
                    <span><b>{it.name}</b> · lotto {it.batch_lotto || it.supplier_lot || "—"} · {it.quantity_used} {it.unit} · scad {it.supplier_expiry || "—"} · {it.ingredient_cost_snapshot != null ? `€${it.ingredient_cost_snapshot.toFixed(2)}` : "costo n/d"}</span>
                    <GitBranch className="w-3.5 h-3.5 text-primary shrink-0" />
                  </button>
                ))}
              </div>
              <h4 className="font-semibold mt-3 mb-1">Snapshot ricetta (immutabile)</h4>
              <div className="text-xs text-muted-foreground">
                {(detail.recipe_snapshot?.ingredients || []).map((i, k) => <span key={k}>{i.name} {i.quantity}{i.unit}{k < detail.recipe_snapshot.ingredients.length - 1 ? " · " : ""}</span>)}
                {detail.recipe_snapshot?.procedure && <p className="mt-2 whitespace-pre-wrap text-foreground">{detail.recipe_snapshot.procedure}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* QR DIALOG */}
      <Dialog open={!!qrProd} onOpenChange={(o) => { if (!o) setQrProd(null); }}>
        <DialogContent className="max-w-sm" data-testid="production-qr-dialog">
          {qrProd && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-primary" />QR produzione</DialogTitle></DialogHeader>
              <div className="flex flex-col items-center gap-3 py-2">
                <div className="p-3 bg-white rounded-xl border border-border"><QRCodeCanvas value={`${window.location.origin}/produzione/${qrProd.public_token}`} size={200} level="M" fgColor="#1B1B1B" /></div>
                <p className="font-mono text-sm">{qrProd.internal_lot}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => copyLink(qrProd.public_token)} data-testid="qr-copy-link"><Copy className="w-4 h-4 mr-1" />Copia link</Button>
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.open(`/produzione/${qrProd.public_token}?etichetta=1`, "_blank")} data-testid="qr-print-label"><Printer className="w-4 h-4 mr-1" />Stampa etichetta</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* TRACCIABILITÀ LOTTO -> PRODUZIONI */}
      <Dialog open={!!trace} onOpenChange={(o) => { if (!o) setTrace(null); }}>
        <DialogContent className="max-w-lg" data-testid="trace-dialog">
          {trace && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><GitBranch className="w-5 h-5 text-primary" />Produzioni collegate · {trace.label}</DialogTitle></DialogHeader>
              <div className="space-y-2">
                {trace.rows.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessuna produzione collegata.</p>}
                {trace.rows.map((r) => (
                  <div key={r.id} className="text-sm border border-border rounded-lg px-3 py-2 flex items-center justify-between" data-testid={`trace-row-${r.id}`}>
                    <span><b className="font-mono">{r.internal_lot}</b> · {r.recipe_name}<br /><span className="text-xs text-muted-foreground">{fmtDate(r.produced_at)} · usati {r.quantity_from_batch} {r.unit} · {r.status}</span></span>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* TRACCIABILITÀ A CASCATA (albero) */}
      <Dialog open={!!tree} onOpenChange={(o) => { if (!o) setTree(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="trace-tree-dialog">
          {tree && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><GitBranch className="w-5 h-5 text-primary" />Tracciabilità a cascata</DialogTitle></DialogHeader>
              <div className="text-sm">
                <div className="font-mono font-semibold">{tree.internal_lot} · {tree.recipe_name}</div>
                <div className="mt-1 space-y-1">
                  {flattenTrace(tree.children, 0).map((row, i) => {
                    const n = row.node;
                    return (
                      <div key={i} className="border-l-2 border-border text-xs" style={{ marginLeft: row.depth * 14, paddingLeft: 10 }} data-testid={`trace-node-${i}`}>
                        <span className="flex items-center gap-1.5 flex-wrap py-0.5">
                          {n.type === "internal"
                            ? <span className="px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-600 text-[10px] font-semibold">semilavorato</span>
                            : <span className="px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 text-[10px] font-semibold">fornitore</span>}
                          <b>{n.name}</b>
                          {n.type === "internal"
                            ? <span className="text-muted-foreground font-mono">{n.internal_lot}</span>
                            : <span className="text-muted-foreground">{n.supplier ? n.supplier + " · " : ""}lotto {n.lotto || "—"}</span>}
                          <span className="text-muted-foreground">· {n.quantity_used} {n.unit}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}

function flattenTrace(nodes, depth, acc) {
  acc = acc || [];
  (nodes || []).forEach((n) => {
    acc.push({ node: n, depth });
    if (n.type === "internal" && n.children && n.children.length) flattenTrace(n.children, depth + 1, acc);
  });
  return acc;
}
